import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

const ACCENT = '#1daf5a';
const ACCENT_LIGHT = '#edfcf4';

const styles = StyleSheet.create({
  page: { padding: 0, fontSize: 10, fontFamily: 'Helvetica', color: '#333' },
  headerBar: { backgroundColor: ACCENT, padding: 30, paddingBottom: 20, color: '#fff' },
  headerTitle: { fontSize: 28, fontWeight: 'bold', fontFamily: 'Helvetica-Bold', color: '#fff' },
  headerSubtitle: { fontSize: 12, color: '#c8f5d8', marginTop: 4 },
  body: { padding: 30 },
  row: { flexDirection: 'row', justifyContent: 'space-between' },
  section: { marginBottom: 15 },
  sectionTitle: { fontSize: 9, color: ACCENT, textTransform: 'uppercase', marginBottom: 4, letterSpacing: 1, fontFamily: 'Helvetica-Bold' },
  bold: { fontWeight: 'bold', fontFamily: 'Helvetica-Bold' },
  // Table
  tableHeader: { flexDirection: 'row', backgroundColor: ACCENT_LIGHT, padding: 8, borderRadius: 4, marginBottom: 4 },
  tableRow: { flexDirection: 'row', padding: 8, borderBottomWidth: 0.5, borderBottomColor: '#eee' },
  colDescription: { flex: 3 },
  colQty: { flex: 1, textAlign: 'center' },
  colPrice: { flex: 1, textAlign: 'right' },
  colAmount: { flex: 1, textAlign: 'right' },
  // Totals
  totalsContainer: { marginTop: 10, alignItems: 'flex-end' },
  totalRow: { flexDirection: 'row', width: 220, justifyContent: 'space-between', paddingVertical: 3 },
  grandTotal: { flexDirection: 'row', width: 220, justifyContent: 'space-between', paddingVertical: 6, backgroundColor: ACCENT, borderRadius: 4, paddingHorizontal: 10, marginTop: 6 },
  grandTotalText: { fontSize: 14, fontWeight: 'bold', fontFamily: 'Helvetica-Bold', color: '#fff' },
  notes: { marginTop: 20, padding: 12, backgroundColor: ACCENT_LIGHT, borderRadius: 4 },
  footerBar: { position: 'absolute', bottom: 0, left: 0, right: 0, backgroundColor: ACCENT, padding: 12, textAlign: 'center' },
  footerText: { fontSize: 8, color: '#fff' },
  dates: { flexDirection: 'row', gap: 40, marginBottom: 20 },
  logo: { width: 50, height: 50, objectFit: 'contain' },
});

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount || 0);

const formatAddress = (addr) => {
  if (!addr) return null;
  return [addr.street, addr.city, addr.state, addr.postcode].filter(Boolean).join(', ');
};

const ModernTemplate = ({ invoice }) => {
  const issuer = invoice.issuerSnapshot || {};
  const org = invoice.organisationSnapshot || {};

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Colored Header */}
        <View style={styles.headerBar}>
          <View style={styles.row}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              {issuer.logoUrl && <Image src={issuer.logoUrl} style={styles.logo} />}
              <View>
                <Text style={styles.headerTitle}>INVOICE</Text>
                <Text style={styles.headerSubtitle}>{invoice.invoiceNumber}</Text>
              </View>
            </View>
            <View style={{ textAlign: 'right' }}>
              <Text style={[styles.bold, { color: '#fff', fontSize: 12 }]}>{issuer.businessName || issuer.name || ''}</Text>
              {issuer.abn && <Text style={{ color: '#c8f5d8' }}>ABN: {issuer.abn}</Text>}
              {issuer.email && <Text style={{ color: '#c8f5d8' }}>{issuer.email}</Text>}
              {issuer.phone && <Text style={{ color: '#c8f5d8' }}>{issuer.phone}</Text>}
            </View>
          </View>
        </View>

        <View style={styles.body}>
          {/* Bill To & Dates */}
          <View style={[styles.row, { marginBottom: 20 }]}>
            <View style={{ flex: 1 }}>
              <Text style={styles.sectionTitle}>Bill To</Text>
              <Text style={styles.bold}>{org.name || ''}</Text>
              {org.abn && <Text>ABN: {org.abn}</Text>}
              {org.contactPerson && <Text>Attn: {org.contactPerson}</Text>}
              {org.email && <Text>{org.email}</Text>}
              {formatAddress(org.address) && <Text>{formatAddress(org.address)}</Text>}
            </View>
            <View style={{ alignItems: 'flex-end' }}>
              <View style={{ marginBottom: 8 }}>
                <Text style={styles.sectionTitle}>Issue Date</Text>
                <Text>{invoice.issueDate ? new Date(invoice.issueDate).toLocaleDateString('en-AU') : ''}</Text>
              </View>
              <View>
                <Text style={styles.sectionTitle}>Due Date</Text>
                <Text style={styles.bold}>{invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString('en-AU') : ''}</Text>
              </View>
            </View>
          </View>

          {/* Line Items */}
          <View style={styles.tableHeader}>
            <Text style={[styles.colDescription, styles.bold]}>Description</Text>
            <Text style={[styles.colQty, styles.bold]}>Qty</Text>
            <Text style={[styles.colPrice, styles.bold]}>Unit Price</Text>
            <Text style={[styles.colAmount, styles.bold]}>Amount</Text>
          </View>
          {(invoice.lineItems || []).map((item, idx) => (
            <View key={idx} style={styles.tableRow}>
              <Text style={styles.colDescription}>{item.description}</Text>
              <Text style={styles.colQty}>{item.quantity}</Text>
              <Text style={styles.colPrice}>{formatCurrency(item.unitPrice)}</Text>
              <Text style={styles.colAmount}>{formatCurrency(item.amount)}</Text>
            </View>
          ))}

          {/* Totals */}
          <View style={styles.totalsContainer}>
            <View style={styles.totalRow}>
              <Text>Subtotal</Text>
              <Text>{formatCurrency(invoice.subtotal)}</Text>
            </View>
            {invoice.gstIncluded && (
              <View style={styles.totalRow}>
                <Text>GST ({invoice.gstRate || 10}%)</Text>
                <Text>{formatCurrency(invoice.gstAmount)}</Text>
              </View>
            )}
            <View style={styles.grandTotal}>
              <Text style={styles.grandTotalText}>Total</Text>
              <Text style={styles.grandTotalText}>{formatCurrency(invoice.total)}</Text>
            </View>
          </View>

          {/* Notes */}
          {invoice.notes && (
            <View style={styles.notes}>
              <Text style={styles.sectionTitle}>Notes</Text>
              <Text>{invoice.notes}</Text>
            </View>
          )}

          {/* Bank Details */}
          {issuer.bankDetails && (issuer.bankDetails.bsb || issuer.bankDetails.accountNumber) && (
            <View style={[styles.section, { marginTop: 15 }]}>
              <Text style={styles.sectionTitle}>Payment Details</Text>
              {issuer.bankDetails.bankName && <Text>Bank: {issuer.bankDetails.bankName}</Text>}
              {issuer.bankDetails.accountName && <Text>Account Name: {issuer.bankDetails.accountName}</Text>}
              {issuer.bankDetails.bsb && <Text>BSB: {issuer.bankDetails.bsb}</Text>}
              {issuer.bankDetails.accountNumber && <Text>Account: {issuer.bankDetails.accountNumber}</Text>}
            </View>
          )}
        </View>

        {/* Footer */}
        <View style={styles.footerBar}>
          <Text style={styles.footerText}>
            {issuer.paymentTerms ? `Payment Terms: ${issuer.paymentTerms} | ` : ''}Thank you for your business
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default ModernTemplate;
